import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertVariant } from 'react-unity';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import { AbstractForm, AbstractFormState, IStateForm } from '../common/AbstractForm';
import AWSAccountsService from '../../services/AWSAccountsService';
import FormModel from '../../utils/FormModel';
import { LinkTreeItem } from '../common/LinkTree';
import CartaId from '../../models/entities/CartaId';
import UserLookupObject from '../../models/UserLookupObject';
import Group from '../../models/Group';
import { RequestAWSAccountModel } from '../../models/viewModels/AWSAccounts/RequestAWSAccountModel';
import AWSAccountConnectivityConfiguration from '../../models/entities/AWSAccountConnectivityConfiguration';
import { AWSEnvironment } from '../../models/enums/AWSEnvironment';

interface EndModal extends AlertBanner {
    redirectionLink?: string;
}

export interface IAWSAccountsStateForm extends IStateForm {
    id: number;
    appName: string;
    accountName: string;
    selectedL3: string;
    selectedL4: string;
    selectedOrganization: string;
    internalOrder?: string;
    environment: AWSEnvironment;
    isManaged: boolean;
    contributorAccessGroup: Group;
    readonlyAccessGroup: Group;
    isRetainedApp?: boolean;
    requiresEc2Instances?: boolean;
    requiresPublicIpAddresses?: boolean;
    requiresNativeContainerServices?: boolean;
    requiresIsolation?: boolean;
    requiresDMZ: boolean;
    regionNames: [];
    numOfDmzPublicIpAddresses?: string;
    vpcSizeId?: string;
    cartaIds: CartaId[];
    pocDurationDays?: string;
    owner: UserLookupObject;
    custodian: UserLookupObject;
    accountUsedFor: string;
    networkConfigList: AWSAccountConnectivityConfiguration[];
    ownerInTransfer?: UserLookupObject | null;
    custodianInTransfer: UserLookupObject | null;
}

export interface AbstractAWSAccountsFormState<T> extends AbstractFormState<T> {
    operationInProgress: boolean;
    endModal: EndModal;
    submissionWarned: boolean;
    submissionAlert: AlertBanner;
    standardConfigurationData: any;
    loadingTracker?: boolean;
    failedTracker?: boolean;
    errorTracker?: string;
    approverOptions?: any[];
    formReady: boolean,
}

export const useAbstractAWSAccountsForm = () => {
    const [state, setState] = useState<AbstractAWSAccountsFormState<IAWSAccountsStateForm>>({
        stateForm: {
            id: null,
            appName: '',
            accountName: '',
            isManaged: null,
            contributorAccessGroup: null,
            readonlyAccessGroup: null,
            selectedL3: '',
            selectedL4: '',
            selectedOrganization: '',
            internalOrder: null,
            environment: null,
            cartaIds: [],
            pocDurationDays: '',
            owner: null,
            custodian: null,
            accountUsedFor: '',
            isRetainedApp: false,
            requiresEc2Instances: false,
            requiresPublicIpAddresses: false,
            requiresNativeContainerServices: false,
            requiresIsolation: false,
            requiresDMZ: false,
            regionNames: [],
            numOfDmzPublicIpAddresses: null,
            vpcSizeId: null,
            networkConfigList: [],
            ownerInTransfer: null,
            custodianInTransfer: null,
        },
        operationInProgress: false,
        endModal: { visible: false },
        submissionWarned: true,  
        submissionAlert: { visible: false },
        standardConfigurationData: null,
        approverOptions: [],
        formReady: true,  
    });

    const awsAccountsService = new AWSAccountsService();

    const abstractFormInstance = new (class extends AbstractForm<
        {},
        AbstractAWSAccountsFormState<IAWSAccountsStateForm>
    > {
        constructor() {
            super({});
            this.state = state;
        }
    })();

    const canValidateEditableFields = () => {
        return state.formReady && state.submissionWarned;
    };

    const [formModel] = useState<FormModel>(
        (abstractFormInstance.formModel = new FormModel({
            appName: {
                getValue: () => state.stateForm.appName,
                validation: {
                    required: () => canValidateEditableFields(),
                    rules: [
                        {
                            assert: () =>
                                !canValidateEditableFields() ||
                                (
                                    state.stateForm.appName?.trim().length >= 3 &&
                                    state.stateForm.appName?.trim().length <= 16
                                ),
                            message: 'Please ensure your response does not exceed 16 characters.',
                        },
                        {
                            assert: () =>
                                !canValidateEditableFields() ||
                                /^[a-z0-9A-Z][a-z0-9A-Z_\s]*$/.test(state.stateForm.appName),
                            message:
                                'Invalid app name format. Only alphanumeric characters, underscores and spaces are allowed.',
                        },
                    ],
                },
            },
            accountName: {
                getValue: () => state.stateForm.accountName,
                validation: {
                    required: () => {
                        const appName = state.stateForm.appName?.trim();
                        return !appName || appName.length === 0;
                    },
                    rules: [
                        {
                            assert: () => {
                                const accountName = state.stateForm.accountName;
                                if (!accountName || accountName.trim().length === 0) {
                                    return true;
                                }
                                return /^[0-9A-Z][0-9A-Z_]{2,49}$/.test(accountName);
                            },
                            message:
                                'Invalid account name format. Only uppercase alphanumeric characters and underscores are allowed.',
                        },
                    ],
                },
            },
            selectedL3GlobalId: {
                getValue: () => state.stateForm.selectedL3,
                validation: {
                    required: () => true,
                },
            },
            selectedL4GlobalId: {
                getValue: () => state.stateForm.selectedL4,
                validation: {
                    required: () => true,
                },
            },
            organizationId: {
                getValue: () => state.stateForm.selectedOrganization,
                validation: {
                    required: () => false,  
                },
            },
            internalOrder: {
                getValue: () => state.stateForm.internalOrder,
                validation: {
                    required: () => false,  
                    rules: [
                        {
                            assert: function(this: any) {
                                const value = state.stateForm.internalOrder;
                                if (!value || (typeof value === 'string' && value.trim().length === 0)) {
                                    return true;
                                }
                                const trimmedValue = value.trim();
                                return /^G8020[0-9]{5}$/.test(trimmedValue);
                            },
                            message: 'Invalid internal order format. Must be G8020 followed by exactly 5 digits.',
                        },
                    ],
                },
            },
            environment: {
                getValue: () => state.stateForm.environment?.value,
                validation: {
                    required: () => true,
                },
            },
            isManaged: {
                getValue: () => state.stateForm.isManaged,
                validation: {
                    required: () => true,
                },
            },
            isRetainedApp: {
                getValue: () => state.stateForm.isRetainedApp,
                validation: {
                    required: () => true,
                },
            },
            requiresEc2Instances: {
                getValue: () => state.stateForm.requiresEc2Instances,
                validation: {
                    required: () => state.stateForm.isManaged === false,
                },
            },
            requiresPublicIpAddresses: {
                getValue: () => state.stateForm.requiresPublicIpAddresses,
                validation: {
                    required: () => state.stateForm.isManaged === false,
                },
            },
            requiresNativeContainerServices: {
                getValue: () => state.stateForm.requiresNativeContainerServices,
                validation: {
                    required: () => state.stateForm.isManaged === false,
                },
            },
            requiresIsolation: {
                getValue: () => state.stateForm.requiresIsolation,
                validation: {
                    required: () => state.stateForm.isManaged === true,
                },
            },
            requiresDMZ: {
                getValue: () => state.stateForm.requiresDMZ,
                validation: {
                    required: () => state.stateForm.isManaged === true,
                },
            },
            numOfDmzPublicIpAddresses: {
                getValue: () => state.stateForm.numOfDmzPublicIpAddresses,
                validation: {
                    required: () =>
                        state.stateForm.isManaged === true &&
                        state.stateForm.requiresDMZ === true,
                    rules: [
                        {
                            assert: () => {
                                if (state.stateForm.isManaged !== true || state.stateForm.requiresDMZ !== true) {
                                    return true;
                                }
                                const num = parseInt(state.stateForm.numOfDmzPublicIpAddresses);
                                return !isNaN(num) && num > 0 && num <= 16;
                            },
                            message: 'Please enter a number between 1 and 16.',
                        },
                    ],
                },
            },
            vpcSizeId: {
                getValue: () => state.stateForm.vpcSizeId,
                validation: {
                    required: () =>
                        state.stateForm.isManaged === true &&
                        (state.stateForm.requiresDMZ === true || state.stateForm.requiresIsolation === true),
                },
            },
            regionNames: {
                getValue: () => state.stateForm.regionNames,
                validation: {
                    required: () =>
                        state.stateForm.isManaged === true &&
                        (state.stateForm.requiresDMZ === true || state.stateForm.requiresIsolation === true),
                },
            },
            cartaIds: {
                getValue: () => state.stateForm.cartaIds,
                validation: {
                    required: () => true,
                },
            },
            pocDurationDays: {
                getValue: () => state.stateForm.pocDurationDays,
                validation: {
                    required: () => state.stateForm.isManaged === false && state.stateForm.environment === AWSEnvironment.PoC,
                },
            },
            ownerUserId: {
                getValue: () => state.stateForm.owner?.id,
                validation: {
                    required: () => true,
                },
            },
            custodianUserId: {
                getValue: () => state.stateForm.custodian?.id,
                validation: {
                    required: () => true,
                },
            },
            accountUsedFor: {
                getValue: () => state.stateForm.accountUsedFor,
                validation: {
                    required: () => true,
                    rules: [
                        {
                            assert: () => {
                                const value = state.stateForm.accountUsedFor;
                                if (!value || (typeof value === 'string' && value.trim().length === 0)) {
                                    return true;
                                }
                                const trimmedLength = value.trim().length;
                                return trimmedLength >= 10 && trimmedLength <= 500;
                            },
                            message: 'Business justification must be between 10 and 500 characters.',
                        },
                    ],
                },
            },
            networkConfig: {
                getValue: () => state.stateForm.networkConfigList,
            },
        }))
    );

    const navigate = useNavigate();

    const getLinkTree = (): LinkTreeItem[] => {
        return [{ text: 'AWS Accounts', to: '/awsAccounts' }];
    };

    const handleStateFormChange = (field: any, value: any) => {
        abstractFormInstance.setState = (newState) => setState((prevState) => ({ ...prevState, ...newState }));
        abstractFormInstance.handleStateFormChange(field, value);
    };

    const getStateFormHandlers = () => {
        const appNameValidation = {
            required: () => true,
            rules: [
                {
                    assert: () => {
                        const appName = state.stateForm.appName;
                        if (!appName || (typeof appName === 'string' && appName.trim().length === 0)) {
                            return true; 
                        }
                        const trimmedLength = appName.trim().length;
                        return trimmedLength >= 3 && trimmedLength <= 16;
                    },
                    message: 'App name must be between 3 and 16 characters.',
                },
                {
                    assert: () => {
                        const appName = state.stateForm.appName;
                        if (!appName || (typeof appName === 'string' && appName.trim().length === 0)) {
                            return true; 
                        }
                        return /^[a-z0-9A-Z][a-z0-9A-Z_\s]*$/.test(appName);
                    },
                    message:
                        'Invalid app name format. Only alphanumeric characters, underscores and spaces are allowed.',
                },
            ],
        };

        const internalOrderValidation = {
            required: () => false,
            rules: [
                {
                    assert: () => {
                        const value = state.stateForm.internalOrder;
                        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
                            return true;
                        }
                        const trimmedValue = value.trim();
                        return /^G8020[0-9]{5}$/.test(trimmedValue);
                    },
                    message: 'Invalid internal order format. Must be G8020 followed by exactly 5 digits.',
                },
            ],
        };

        return {
            appName: {
                value: state.stateForm.appName,
                validation: appNameValidation,
                onChange: (e) => {
                    const appName = e.target.value;
                    handleStateFormChange('appName', appName);
                },
            },
            accountName: {
                value: state.stateForm.accountName,
                validation: formModel.fields.accountName.validation,
                onChange: (e) => {
                    const accountName = e.target.value.toUpperCase().replace(' ', '_');
                    handleStateFormChange('accountName', accountName);
                },
            },
            selectedL3: {
                value: state.stateForm.selectedL3,
                validation: formModel.fields.selectedL3GlobalId.validation,
                onChange: (orgId: string) => {
                    handleStateFormChange('selectedL3', orgId);
                },
            },
            selectedL4: {
                value: state.stateForm.selectedL4,
                validation: formModel.fields.selectedL4GlobalId.validation,
                onChange: (orgId: string) => {
                    handleStateFormChange('selectedL4', orgId);
                },
            },
            selectedOrganization: {
                value: state.stateForm.selectedOrganization,
                validation: formModel.fields.organizationId.validation,
                onChange: (orgId: string) => {
                    handleStateFormChange('selectedOrganization', orgId);
                },
            },
            internalOrder: {
                value: state.stateForm.internalOrder,
                validation: internalOrderValidation,
                onChange: (e) => {
                    handleStateFormChange('internalOrder', e.target.value);
                },
            },
            environment: {
                value: state.stateForm.environment?.value,
                validation: formModel.fields.environment.validation,
                onChange: (event) => {
                    const env = new AWSEnvironment().fromValue(Number(event.target.value));
                    const dependentFields = ['pocDurationDays'];
                    const connectedFields = ['environment', ...dependentFields];
                    const values = [env, ...dependentFields.map(() => null)];
                    handleStateFormChange(connectedFields, values);
                },
            },
            isManaged: {
                value: state.stateForm.isManaged,
                validation: formModel.fields.isManaged.validation,
                onChange: (event) => {
                    const isManaged = event.target.value === 'true';
                    const dependentFields = [
                        'pocDurationDays',
                        'requiresEc2Instances',
                        'requiresPublicIpAddresses',
                        'requiresNativeContainerServices',
                        'regionNames',
                    ];
                    const connectedFields = ['isManaged', ...dependentFields];
                    const values = [
                        isManaged,
                        isManaged ? state.stateForm.pocDurationDays : null,
                        isManaged ? state.stateForm.requiresEc2Instances : false,
                        isManaged ? state.stateForm.requiresPublicIpAddresses : false,
                        isManaged ? state.stateForm.requiresNativeContainerServices : false,
                        isManaged ? state.stateForm.regionNames : [],
                    ];
                    handleStateFormChange(connectedFields, values);
                },
            },
            contributorAccessGroup: {
                value: state.stateForm.contributorAccessGroup?.displayName,
                validation: { required: () => false },
                onChange: (value) => {
                    handleStateFormChange('contributorAccessGroup', value);
                },
            },
            readonlyAccessGroup: {
                value: state.stateForm.readonlyAccessGroup?.displayName,
                validation: { required: () => false },
                onChange: (value) => {
                    handleStateFormChange('readonlyAccessGroup', value);
                },
            },
            isRetainedApp: {
                value: state.stateForm.isRetainedApp,
                validation: formModel.fields.isRetainedApp.validation,
                onChange: (event) => {
                    handleStateFormChange('isRetainedApp', event.target.value);
                },
            },
            requiresEc2Instances: {
                value: state.stateForm.requiresEc2Instances,
                validation: formModel.fields.requiresEc2Instances.validation,
                onChange: (event) => {
                    handleStateFormChange('requiresEc2Instances', event.target.value);
                },
            },
            requiresPublicIpAddresses: {
                value: state.stateForm.requiresPublicIpAddresses,
                validation: formModel.fields.requiresPublicIpAddresses.validation,
                onChange: (event) => {
                    handleStateFormChange('requiresPublicIpAddresses', event.target.value);
                },
            },
            requiresNativeContainerServices: {
                value: state.stateForm.requiresNativeContainerServices,
                validation: formModel.fields.requiresNativeContainerServices.validation,
                onChange: (event) => {
                    handleStateFormChange('requiresNativeContainerServices', event.target.value);
                },
            },
            requiresIsolation: {
                value: state.stateForm.requiresIsolation,
                validation: formModel.fields.requiresIsolation.validation,
                onChange: (event) => {
                    const isDMZRequired = state.stateForm.requiresDMZ;
                    const requiresIsolation = event.target.value === 'true';
                    const dependentFields = ['regionNames', 'vpcSizeId'];
                    const connectedFields = ['requiresIsolation', ...dependentFields];
                    const values = [
                        requiresIsolation,
                        !isDMZRequired && !requiresIsolation ? [] : state.stateForm.regionNames,
                        !isDMZRequired && !requiresIsolation ? null : state.stateForm.vpcSizeId,
                    ];

                    handleStateFormChange(connectedFields, values);
                },
            },
            requiresDMZ: {
                value: state.stateForm.requiresDMZ,
                validation: formModel.fields.requiresDMZ.validation,
                onChange: (event) => {
                    const isDMZRequired = event.target.value === 'true';
                    const requiresIsolation = state.stateForm.requiresIsolation;
                    const dependentFields = ['numOfDmzPublicIpAddresses', 'regionNames', 'vpcSizeId'];
                    const connectedFields = ['requiresDMZ', ...dependentFields];
                    const values = [
                        isDMZRequired,
                        null,
                        !isDMZRequired && !requiresIsolation ? [] : state.stateForm.regionNames,
                        !isDMZRequired && !requiresIsolation ? null : state.stateForm.vpcSizeId,
                    ];
                    handleStateFormChange(connectedFields, values);
                },
            },
            regionNames: {
                value: state.stateForm.regionNames,
                validation: formModel.fields.regionNames.validation,
                onChange: (event) => {
                    const currentRegionNames = state.stateForm.regionNames || [];
                    let regionNames = [];
                    if (!currentRegionNames.find((r) => r === event.target.value)) {
                        regionNames = [...currentRegionNames, event.target.value];
                    } else {
                        regionNames = currentRegionNames.filter((r) => r !== event.target.value);
                    }
                    handleStateFormChange('regionNames', regionNames);
                },
            },
            numOfDmzPublicIpAddresses: {
                value: state.stateForm.numOfDmzPublicIpAddresses,
                validation: formModel.fields.numOfDmzPublicIpAddresses.validation,
                onChange: (event) => {
                    handleStateFormChange('numOfDmzPublicIpAddresses', event.target.value);
                },
            },
            vpcSizeId: {
                value: state.stateForm.vpcSizeId,
                validation: formModel.fields.vpcSizeId.validation,
                onChange: (event) => {
                    handleStateFormChange('vpcSizeId', event.target.value);
                },
            },
            cartaIds: {
                value: state.stateForm.cartaIds,
                validation: formModel.fields.cartaIds.validation,
                onChange: (cartaIds) => {
                    if (cartaIds.length === 1 && cartaIds[0]?.cartaAppId === state.stateForm.cartaIds[0]?.cartaAppId) {
                        const newComment = cartaIds[0].comment;
                        cartaIds[0] = state.stateForm.cartaIds[0];
                        cartaIds[0].comment = newComment;
                    }
                    handleStateFormChange('cartaIds', cartaIds);
                },
            },
            pocDurationDays: {
                value: state.stateForm.pocDurationDays,
                validation: formModel.fields.pocDurationDays.validation,
                onChange: (event) => {
                    handleStateFormChange('pocDurationDays', event.target.value);
                },
            },
            owner: {
                value: state.stateForm.owner?.userPrincipalName,
                validation: formModel.fields.ownerUserId.validation,
                onChange: (value) => {
                    handleStateFormChange('owner', value);
                },
            },
            custodian: {
                value: state.stateForm.custodian?.userPrincipalName,
                validation: formModel.fields.custodianUserId.validation,
                onChange: (value) => {
                    handleStateFormChange('custodian', value);
                },
            },
            accountUsedFor: {
                value: state.stateForm.accountUsedFor,
                validation: formModel.fields.accountUsedFor.validation,
                onChange: (event) => {
                    handleStateFormChange('accountUsedFor', event.target.value);
                },
            },
            networkConfig: {
                network: state.stateForm.networkConfigList,
                onChange: (net: AWSAccountConnectivityConfiguration[]) => {
                    handleStateFormChange('networkConfigList', net);
                },
            },
            ownerInTransfer: {
                value: state.stateForm.ownerInTransfer,
                validation: {
                    required: true,
                },
                onChange: (value: UserLookupObject | null) => {
                    handleStateFormChange('ownerInTransfer', value);
                },
            },
            custodianInTransfer: {
                value: state.stateForm.custodianInTransfer,
                validation: {
                    required: false,
                },
                onChange: (value: UserLookupObject | null) => {
                    handleStateFormChange('custodianInTransfer', value);
                },
            },
        };
    };

    const submitAccount = async (accountRequest: RequestAWSAccountModel) => {
        return await awsAccountsService.submitAccountRequest(accountRequest);
    };

    const userNotAllowed = () => {
        setState((prevState) => ({
            ...prevState,
            submissionAlert: {
                visible: true,
                variant: 'error',
                text: 'This user is not allowed to enter this page.',
            },
        }));
        setTimeout(() => {
            navigate('/awsAccounts');
        }, 5000);
    };

    const handleEndModal = (text: string, variant: AlertVariant, timeout: number, link: string) => {
        setState((prevState) => ({
            ...prevState,
            endModal: {
                visible: true,
                text,
                variant,
                redirectionLink: link,
            },
        }));
    };

    const endAndRedirect = () => {
        const link = state.endModal.redirectionLink;
        setState((prevState) => ({
            ...prevState,
            endModal: { visible: false },
        }));
        navigate(link || '/');
    };

    const setSubmissionAlertInProgress = (message: string) => {
        window.scroll(0, 0);
        setState((prevState) => ({
            ...prevState,
            operationInProgress: true,
            submissionAlert: {
                visible: true,
                text: message,
            },
        }));
    };

    const setSubmissionAlertError = (message: string) => {
        setState((prevState) => ({
            ...prevState,
            submissionAlert: {
                visible: true,
                text: message,
                variant: 'error',
            },
        }));
    };

    const handleSubmissionAlertClose = () => {
        setState((prevState) => ({
            ...prevState,
            submissionAlert: {
                visible: false,
            },
        }));
    };

    const isValid = () => {
        return formModel.isValid();
    };

    return {
        state,
        formModel,
        setState,
        getLinkTree,
        handleStateFormChange,
        getStateFormHandlers,
        handleEndModal,
        userNotAllowed,
        endAndRedirect,
        setSubmissionAlertInProgress,
        setSubmissionAlertError,
        handleSubmissionAlertClose,
        isValid,
        submitAccount,
    };
};
